import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle, CheckCircle, RefreshCw, Filter, Clock, Zap, Server, MessageSquare, CreditCard, Shield, ChevronDown, Eye, Check, Activity, Bell, FileUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_CONFIG = {
  CRITICAL: { color: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500', label: 'Critical' },
  ERROR: { color: 'bg-orange-100 text-orange-800 border-orange-200', dot: 'bg-orange-500', label: 'Error' },
  WARNING: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500', label: 'Warning' },
  INFO: { color: 'bg-blue-100 text-blue-800 border-blue-200', dot: 'bg-blue-500', label: 'Info' },
};

const SERVICE_ICONS = {
  api: Server, twilio: MessageSquare, fast2sms: MessageSquare, razorpay: CreditCard,
  meta: Zap, meta_capi: Zap, firebase_push: Zap, surepass: Shield, business_logic: AlertTriangle,
};

const CATEGORY_LABELS = {
  API_ERROR: 'API Error', INTEGRATION_FAILURE: 'Integration', BUSINESS_LOGIC: 'Business Logic',
};

function timeAgo(ts) {
  if (!ts) return '';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className={`rounded-xl border p-4 ${color}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium opacity-70">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <Icon className="w-8 h-8 opacity-30" />
      </div>
    </div>
  );
}

function ErrorRow({ error, onResolve }) {
  const [expanded, setExpanded] = useState(false);
  const sev = SEVERITY_CONFIG[error.severity] || SEVERITY_CONFIG.INFO;
  const SvcIcon = SERVICE_ICONS[error.service] || Server;

  return (
    <div className="border rounded-lg mb-2 overflow-hidden" data-testid={`error-row-${error.error_id}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sev.dot}`} />
        <SvcIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-gray-900 truncate">{error.title}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${sev.color}`}>{sev.label}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{error.service}</span>
            {error.retry_status === 'succeeded' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Auto-fixed</span>
            )}
            {error.retry_status === 'retrying' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Retrying...</span>
            )}
            {error.retry_status === 'failed' && error.max_retries > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Retries exhausted</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{error.message}</p>
        </div>
        <span className="text-xs text-gray-400 flex-shrink-0">{timeAgo(error.timestamp)}</span>
        {!error.resolved && (
          <button
            onClick={(e) => { e.stopPropagation(); onResolve(error.error_id); }}
            className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 transition-colors flex-shrink-0"
            data-testid={`resolve-btn-${error.error_id}`}
          >
            <Check className="w-3 h-3" />
          </button>
        )}
        {error.resolved && (
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
        )}
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {expanded && (
        <div className="px-4 py-3 bg-gray-50 border-t text-xs space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><span className="text-gray-500">Category:</span> <span className="font-medium">{CATEGORY_LABELS[error.category]}</span></div>
            <div><span className="text-gray-500">Error ID:</span> <span className="font-mono">{error.error_id}</span></div>
            {error.endpoint && <div><span className="text-gray-500">Endpoint:</span> <span className="font-mono">{error.endpoint}</span></div>}
            {error.status_code && <div><span className="text-gray-500">Status:</span> <span className="font-mono">{error.status_code}</span></div>}
            {error.retry_count > 0 && <div><span className="text-gray-500">Retries:</span> {error.retry_count}/{error.max_retries}</div>}
            {error.resolved_by && <div><span className="text-gray-500">Resolved by:</span> <span className="font-medium">{error.resolved_by}</span></div>}
          </div>
          {error.details && Object.keys(error.details).length > 0 && (
            <div className="mt-2">
              <p className="text-gray-500 mb-1">Details:</p>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto text-xs max-h-48">
                {JSON.stringify(error.details, null, 2)}
              </pre>
            </div>
          )}
          <div className="text-gray-400">
            Logged: {error.timestamp ? new Date(error.timestamp).toLocaleString() : 'N/A'}
            {error.resolved_at && ` | Resolved: ${new Date(error.resolved_at).toLocaleString()}`}
          </div>
        </div>
      )}
    </div>
  );
}

export function ErrorLogsContent() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [errors, setErrors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ severity: '', category: '', service: '', resolved: '' });
  const [page, setPage] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const limit = 30;

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/error-logs/stats?hours=24`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error('Stats fetch error:', e); }
  }, [token]);

  const fetchErrors = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: String(limit), skip: String(page * limit) });
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.category) params.append('category', filters.category);
      if (filters.service) params.append('service', filters.service);
      if (filters.resolved !== '') params.append('resolved', filters.resolved);

      const res = await fetch(`${API}/api/error-logs?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setErrors(data.errors || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error('Errors fetch error:', e); }
  }, [token, filters, page]);

  const resolveError = async (errorId) => {
    try {
      await fetch(`${API}/api/error-logs/${errorId}/resolve`, { method: 'POST', headers });
      fetchErrors();
      fetchStats();
    } catch (e) { console.error('Resolve error:', e); }
  };

  const resolveAll = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.service) params.append('service', filters.service);
      await fetch(`${API}/api/error-logs/resolve-all?${params}`, { method: 'POST', headers });
      fetchErrors();
      fetchStats();
    } catch (e) { console.error('Resolve all error:', e); }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStats(), fetchErrors()]).finally(() => setLoading(false));
  }, [fetchStats, fetchErrors]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(() => { fetchStats(); fetchErrors(); }, 30000);
    return () => clearInterval(iv);
  }, [autoRefresh, fetchStats, fetchErrors]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="error-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="error-logs-title">System Monitor</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time error tracking, auto-retry, and push alerts</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              autoRefresh ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}
            data-testid="auto-refresh-toggle"
          >
            <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
            {autoRefresh ? 'Live' : 'Paused'}
          </button>
          <button
            onClick={() => { fetchStats(); fetchErrors(); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="stats-grid">
          <StatCard label="Total (24h)" value={stats.total} icon={AlertTriangle} color="bg-white border-gray-200" />
          <StatCard label="Critical" value={stats.by_severity?.CRITICAL || 0} icon={Zap} color="bg-red-50 border-red-200" />
          <StatCard label="Unresolved" value={stats.unresolved} icon={Clock} color="bg-orange-50 border-orange-200" />
          <StatCard label="Auto-Fixed" value={stats.auto_resolved} icon={CheckCircle} color="bg-green-50 border-green-200" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="filters">
        <Filter className="w-4 h-4 text-gray-400" />
        <select value={filters.severity} onChange={e => { setFilters(f => ({ ...f, severity: e.target.value })); setPage(0); }}
          className="text-sm border rounded-lg px-3 py-2 bg-white" data-testid="filter-severity">
          <option value="">All Severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="ERROR">Error</option>
          <option value="WARNING">Warning</option>
          <option value="INFO">Info</option>
        </select>
        <select value={filters.category} onChange={e => { setFilters(f => ({ ...f, category: e.target.value })); setPage(0); }}
          className="text-sm border rounded-lg px-3 py-2 bg-white" data-testid="filter-category">
          <option value="">All Categories</option>
          <option value="API_ERROR">API Errors</option>
          <option value="INTEGRATION_FAILURE">Integration</option>
          <option value="BUSINESS_LOGIC">Business Logic</option>
        </select>
        <select value={filters.service} onChange={e => { setFilters(f => ({ ...f, service: e.target.value })); setPage(0); }}
          className="text-sm border rounded-lg px-3 py-2 bg-white" data-testid="filter-service">
          <option value="">All Services</option>
          <option value="api">API</option>
          <option value="twilio">Twilio</option>
          <option value="fast2sms">Fast2SMS</option>
          <option value="razorpay">Razorpay</option>
          <option value="meta">Meta Ads</option>
          <option value="meta_capi">Meta CAPI</option>
          <option value="firebase_push">Firebase</option>
          <option value="surepass">Surepass</option>
        </select>
        <select value={filters.resolved} onChange={e => { setFilters(f => ({ ...f, resolved: e.target.value })); setPage(0); }}
          className="text-sm border rounded-lg px-3 py-2 bg-white" data-testid="filter-resolved">
          <option value="">All Status</option>
          <option value="false">Unresolved</option>
          <option value="true">Resolved</option>
        </select>
        {total > 0 && errors.some(e => !e.resolved) && (
          <button onClick={resolveAll} className="ml-auto text-sm px-3 py-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 font-medium"
            data-testid="resolve-all-btn">
            Resolve All ({errors.filter(e => !e.resolved).length})
          </button>
        )}
      </div>

      {/* Error List */}
      <div className="space-y-0" data-testid="error-list">
        {loading ? (
          <div className="text-center py-16 text-gray-400">
            <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-3" />
            <p>Loading errors...</p>
          </div>
        ) : errors.length === 0 ? (
          <div className="text-center py-16" data-testid="no-errors">
            <CheckCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
            <p className="text-lg font-semibold text-gray-900">All systems operational</p>
            <p className="text-sm text-gray-500 mt-1">No errors logged in the selected time period</p>
          </div>
        ) : (
          errors.map(err => <ErrorRow key={err.error_id} error={err} onResolve={resolveError} />)
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t" data-testid="pagination">
          <p className="text-sm text-gray-500">Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}</p>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 text-sm rounded border disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 text-sm rounded border disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Tabbed Monitor Page ──
import SpecialOpsTab from '@/components/settings/SpecialOpsTab';
import CustomerCommunicationTab from '@/components/settings/CustomerCommunicationTab';
import SystemMonitorTab from '@/components/settings/SystemMonitorTab';
import LeadMigration from './LeadMigration';
import NotificationConfigPage from './NotificationConfigPage';

export default function MonitorPage() {
  const [activeTab, setActiveTab] = useState('error-logs');

  const tabs = [
    { id: 'error-logs', label: 'Error Logs', icon: AlertTriangle },
    { id: 'system-monitor', label: 'System Monitor', icon: Activity },
    { id: 'communication', label: 'Communication', icon: MessageSquare },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'special-ops', label: 'Special Ops', icon: Zap },
    { id: 'lead-migration', label: 'Lead Migration', icon: FileUp },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Monitor</h1>
              <p className="text-sm text-gray-500">System monitoring, communications, and operations</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-1 p-1 bg-white rounded-xl border w-fit shadow-sm flex-wrap">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-slate-800 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`monitor-${tab.id}-tab`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'error-logs' && <ErrorLogsContent />}

        {activeTab === 'system-monitor' && (
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <SystemMonitorTab />
          </div>
        )}

        {activeTab === 'communication' && (
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <CustomerCommunicationTab />
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="bg-white rounded-xl border shadow-sm">
            <NotificationConfigPage embedded={true} />
          </div>
        )}

        {activeTab === 'special-ops' && (
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <SpecialOpsTab />
          </div>
        )}

        {activeTab === 'lead-migration' && (
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <LeadMigration />
          </div>
        )}
      </div>
    </div>
  );
}
